import { StatusBadge } from './StatusBadge';

/**
 * Ordinal helper: 1 -> "1st", 2 -> "2nd", 3 -> "3rd", 4 -> "4th", ...
 */
function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

/**
 * Count of non-cancelled payment requests on the invoice.
 */
function activePaymentRequestCount(invoice) {
  const prs = invoice?.payment_requests || [];
  return prs.filter(pr => (pr?.status || '').toLowerCase() !== 'cancelled').length;
}

/**
 * Compute the "1st/2nd/3rd payment" qualifier shown next to the status badge.
 * - Only when there is at least one non-cancelled payment request.
 * - Hidden once the invoice is fully paid (status "paid"), to keep the badge clean.
 * - Hidden on cancelled invoices.
 */
export function paymentRoundQualifier(invoice) {
  if (!invoice) return null;
  const status = (invoice.status || '').toLowerCase();
  if (status === 'paid' || status === 'cancelled') return null;
  const n = activePaymentRequestCount(invoice);
  if (n <= 0) return null;
  return `${ordinal(n)} payment`;
}

/**
 * InvoiceStatusBadge — renders the canonical StatusBadge plus an optional
 * "— 1st payment / 2nd payment / …" qualifier computed from
 * invoice.payment_requests[] (non-cancelled only).
 *
 * The canonical status value in the DB is NOT touched: filters and reports
 * keep working unchanged. The qualifier is presentation-only.
 */
export function InvoiceStatusBadge({ invoice, className = '' }) {
  if (!invoice) return null;
  const qualifier = paymentRoundQualifier(invoice);
  return (
    <span
      data-testid={`invoice-status-${invoice.id || 'unknown'}`}
      className={`inline-flex items-center gap-1.5 flex-wrap ${className}`}
    >
      <StatusBadge status={invoice.effective_status || invoice.status} />
      {qualifier && (
        <span
          data-testid={`invoice-payment-round-${invoice.id || 'unknown'}`}
          className="text-[11px] font-medium text-gray-500 whitespace-nowrap"
        >
          — {qualifier}
        </span>
      )}
    </span>
  );
}
